import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import * as moment from 'moment';
import { BehaviorSubject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { SnackbarComponent } from '../components/shared/snackbar/snackbar.component';
import { CommonService } from './common.service';

@Injectable({
  providedIn: 'root'
})
export class PrinterService {
  baseUrl = environment.baseUrl;
  totalPrinters: number;
  outOfWarrantyDevices: number;
  printerInfoSource = new BehaviorSubject({});
  printerInfo$ = this.printerInfoSource.asObservable();
  hasServiceCoverage = false;
  today: any;
  todayDate = new Date();
  alertId: any;
  firmwareInfoSource = new BehaviorSubject({});
  firmwareInfo$ = this.firmwareInfoSource.asObservable();
  constructor(private http: HttpClient,
    private commonService: CommonService,
    private snackbar: MatSnackBar) { }

  getPrinters() {
    return this.http.get<any>(this.baseUrl + '/services/core/users/api/v1/reseller/printers').pipe(
      tap(res => {
        this.totalPrinters = res.result.length;
        this.outOfWarrantyDevices = res.result.expired_devices
      })
    )
  }

  initializeObservables() {
    this.printerInfoSource = new BehaviorSubject({});
    this.printerInfo$ = this.printerInfoSource.asObservable();

    this.firmwareInfoSource = new BehaviorSubject({});
    this.firmwareInfo$ = this.firmwareInfoSource.asObservable();
  }

  storePrinterInfo(PrinterInfo) {
    this.printerInfoSource.next(PrinterInfo);
  }

  storeFirmWareInfo(firmwareInfo) {
    this.firmwareInfoSource.next(firmwareInfo);
  }


  getPrinterInfo(printerId) {
    return this.http.get<any>(this.baseUrl + '/services/core/users/api/v1/printer_views/' + printerId).pipe(tap(data => {
      this.hasServiceCoverage = this.checkHasCoverage(data?.result?.printer_view);
    }))
  }
  getPrinterInkLevel(device_id) {
    return this.http.get<any>(this.baseUrl + `/services/core/users/api/v1/printer_ink_info?device_id=${device_id}`)
  }

  checkHasCoverage(coverage) {
    let currentDate = moment(new Date()).format('YYYY-MM-DDTHH:mm:ssZ');
    let contractEndDate = moment(new Date(coverage?.contracts_end_date?.replace(/-/g, "/"))).format('YYYY-MM-DDTHH:mm:ssZ');
    let warrantyEndDate = moment(new Date(coverage?.warranty_end_date?.replace(/-/g, "/"))).format('YYYY-MM-DDTHH:mm:ssZ');
    if (this.commonService.userInfo.is_upselling) {
      if (((coverage?.warranty_end_date != null && moment(warrantyEndDate).isSameOrAfter(currentDate)) ||
        (coverage?.contracts_end_date != null && moment(contractEndDate).isSameOrAfter(currentDate)))) {
        return true;
      }

      return false;
    }
    return true;
  }

  getPrintCareVersion(printerId) {
    return this.http.get<any>(this.baseUrl + '/services/core/api/printos/v1/get_print_care_version/' + printerId)
  }

  getPrinterInfWithSiteData(printerId) {
    return this.http.get<any>(this.baseUrl + '/services/core/api/printos/v1/printers/' + printerId)
  }

  downloadEOIReport(printer) {
    const paramsObject = {
      model: printer.product_number,
      serial_number: printer.serial_number
    }
    let params = new HttpParams();
    for (let key in paramsObject) {
      params = params.set(key, paramsObject[key]);
    }
    return this.http.get<Blob>(this.baseUrl + '/services/eoi/api/v1/download', { observe: 'response', responseType: 'blob' as 'json', params })
  }

  getContracts(printerId) {
    return this.http.get<any>(this.baseUrl + `/services/seals/api/v1/printers/contracts?device_id=${printerId}`)
  }

  getProactiveAlerts(printerId) {
    return this.http.get<any>(this.baseUrl + `/services/seals/api/v1/alerts/new_proactive_alerts?device_id=${printerId}`)
  }

  getSolution(alertId) {
    return this.http.get<any>(this.baseUrl + `/services/seals/api/v1/alerts/${alertId}/get_solution`)
  }

  getCaseID(element) {
    return this.http.get<any>(this.baseUrl + `/services/case/api/v1/case_id_from_alert?error_code=${element?.error_code}&timestamp=${element?.date}&event_name=${element?.event_name}&device_id=${element?.device_id}`)
  }

  getCaseIDFromAction(actionID) {
    return this.http.get<any>(this.baseUrl + `/services/case/api/v1/hp_case_of_alert?action_id=${actionID}`)
  }

  getCaseAlertID(alertId) {
    return this.http.get<any>(this.baseUrl + `/services/case/api/v1/hp_case_of_alert?alert_id=${alertId}`)
  }

  getPrinterMaintenances(printerInfo) {
    return this.http.get<any>(this.baseUrl + `/services/seals/api/v1/printers/maintenances?product_number=${printerInfo.product_number}&serial_number=${printerInfo.serial_number}&is_polestar_printer=${printerInfo.is_polestar_printer}`)
  }

  getPrinterMaintenanceCount(printerInfo) {
    return this.http.get<any>(this.baseUrl + `/services/seals/api/v1/printers/maintenance_count?product_number=${printerInfo.product_number}&serial_number=${printerInfo.serial_number}&device_id=${printerInfo.device_id}`)
  }

  getRecommendedAction(printerInfo) {
    return this.http.get<any>(this.baseUrl + `/services/seals/api/v1/printers/recommended_maintenances?product_number=${printerInfo.product_number}&serial_number=${printerInfo.serial_number}`)
  }

  uploadSiteReport(uploadData) {
    return this.http.post<any>(this.baseUrl + '/services/site_preparation/api/v1/site_preparations/upload_report', uploadData);
  }

  downloadReport(site) {
    return this.http.get<any>(this.baseUrl + `/services/site_preparation/api/v1/site_preparations/site_preparation_report?printer_id=${site.device_id}`)
  }

  downLoadSiteReport(url) {
    return this.http.get<Blob>(url, { observe: 'response', responseType: 'blob' as 'json' });
  }

  deletePrinterReport(site) {
    return this.http.delete<any>(this.baseUrl + `/services/site_preparation/api/v1/site_preparations/delete_site_prep_link?device_id=${site.device_id}`);
  }

  successModal(message, subHeading) {
    this.alertModal({
      heading: message,
      subHeading,
      isSuccess: true
    });
  }

  failedModal(message, subHeading) {
    this.alertModal({
      heading: message,
      subHeading,
      isSuccess: false
    });
  }

  alertModal(data) {
    const snackRef = this.snackbar.openFromComponent(SnackbarComponent, {
      data: {
        ...data,
        showCloseButton: true,
        close: () => snackRef.dismiss()
      },
      duration: 10000,
      panelClass: 'pending-snackbar',
    });
  }

  updateColor(progress) {
    if (progress.green_color == '0%' && progress.orange_color == '0%') {
      return '#BE1313';
    }
    else if (progress.green_color == '0%' && progress.red_color == '0%') {
      return '#D06702';
    }
    else {
      return '#1C7A17';
    }
  }

  getPrinterCases(device_id) {
    return this.http.get<any>(this.baseUrl + `/services/case/api/v1/printer_cases?device_id=${device_id}`)
  }

  getCustomerCases(customerId) {
    return this.http.get<any>(this.baseUrl + `/services/case/api/v1/customer_cases?customer_id=${customerId}`)
  }


  getSubPrintConsumption(dateFormData) {
    return this.http.post<any>(this.baseUrl + '/services/core/users/api/v1/substrate_consumption', dateFormData)
  }

  getSubInkConsumption(dateFormData) {
    return this.http.post<any>(this.baseUrl + '/services/core/users/api/v1/ink_consumption', dateFormData)
  }

  get_formatted_date(date) {
    return moment(date).format('DD MMM YYYY');
  }

  formatTodayDate(date) {
    return moment(new Date(date)).format('YYYY-MM-DDTHH:MM:SS') + 'Z'
  }

  getFeedback(action_id) {
    return this.http.get<any>(this.baseUrl + '/services/seals/api/v1/feedback?action_id=' + action_id)
  }

  saveFeedback(body) {
    return this.http.post<any>(this.baseUrl + '/services/seals/api/v1/save_feedback', body)
  }

  getCase_supported_models() {
    return this.http.get<any>(this.baseUrl + `/services/sc/api/v1/case_supported_models`)
  }

  solveError(id) {
    return this.http.post<any>(this.baseUrl + `/services/seals/api/v1/alerts/${id}/solve_error`, '');
  }

  getPrinterPlanDetails(printerID) {
    return this.http.get<any>(this.baseUrl + `/services/pricebook/api/v1/printer_plan_details?device_id=${printerID}`);
  }

  cancelServicePlan(payload) {
    return this.http.post<any>(this.baseUrl + `/services/pricebook/api/v1/cancel_plan`, payload)
  }

  getUsageData(params) {
    return this.http.post<any>(this.baseUrl + `/services/core/users/api/v1/printer_substrate_and_ink_consumption`, null, { params })
  }

  getInstalledUpgrades(product_number, serial_number) {
    return this.http.get<any>(this.baseUrl + `/services/core/users/api/v1/printer/get_installed_upgrades?product_number=${product_number}&serial_number=${serial_number}`)
  }

  getInstalledUpgradesByCustomerID(customer_id) {
    return this.http.get<any>(this.baseUrl + `/services/core/users/api/v1/printer/get_installed_upgrades?customer_id=${customer_id}`)
  }

  modifiedEventWithoutLocale(type, userReplaceable) {
    let typeNLowercase = type.toLowerCase()
    switch (typeNLowercase) {
      case 'systemerrors':
        return 'System Error'
      case 'maintenance':
        if (userReplaceable) {
          return 'User Maintenance'
        }
        return 'Service Maintenance'
      case 'fwupgrade':
        return 'Firmware Update'
      case 'targetedaction':
        return 'Targeted Actions'
      case 'targetedactions':
        return 'Targeted Actions'
      case 'lowinklevel':
        return 'Low ink level'
    }

  }

  modifiedEventType(type, userReplaceable) {
    let typeNLowercase = type.toLowerCase()
    switch (typeNLowercase) {
      case 'systemerrors':
        return 'System Error'
      case 'maintenance':
        if (userReplaceable) {
          return 'User Maintenance'
        }
        return 'Service Maintenance'
      case 'fwupgrade':
        return 'Firmware Update'
      case 'targetedaction':
        return 'Targeted action'
      case 'lowinklevel':
        return 'Low ink level'
    }

  }

  getDeviceNPStatus(device_id) {
    return this.http.get<any>(this.baseUrl + `/services/core/api/printos/v1/get_device_np_status/${device_id}`)
  }


}

